import React from "react";
import { Link } from "react-router-dom";

const TYPES = [
  {
    key: "essential",
    title: "Essential",
    badge: "Always on",
    desc: "Required for the site to load and function — page navigation, your theme preference (dark/light), session continuity, anti-spam, and security.",
    items: [
      { name: "cx_theme", purpose: "Stores your dark/light mode choice", retention: "Until cleared", provider: "Capexara AI" },
      { name: "capexara_admin_token", purpose: "Authenticates the admin dashboard session", retention: "Until sign-out", provider: "Capexara AI" },
      { name: "capexara_cookie_consent", purpose: "Remembers your cookie choices on this browser", retention: "12 months", provider: "Capexara AI" },
    ],
  },
  {
    key: "analytics",
    title: "Analytics",
    badge: "Opt-in",
    desc: "Helps us understand how the site is used so we can improve it. We rely on aggregate, pseudonymous metrics — never personal sales calls based on your browsing.",
    items: [
      { name: "_ga, _ga_*", purpose: "Distinguishes unique users for Google Analytics 4", retention: "13 months", provider: "Google" },
      { name: "_clck, _clsk, MUID", purpose: "Microsoft Clarity heatmap & session-replay analytics", retention: "1 year", provider: "Microsoft" },
    ],
  },
  {
    key: "marketing",
    title: "Marketing",
    badge: "Opt-in",
    desc: "Helps us measure campaigns and reach you with relevant content on other sites. Disabled until you opt in.",
    items: [
      { name: "li_at, bcookie, lidc", purpose: "LinkedIn Insight Tag & retargeting", retention: "Up to 24 months", provider: "LinkedIn" },
      { name: "_fbp", purpose: "Meta retargeting pixel", retention: "90 days", provider: "Meta" },
    ],
  },
];

export default function Cookies() {
  const openPrefs = (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent("capexara:open-cookie-prefs"));
  };

  return (
    <section className="legal-page" data-testid="cookies-page">
      <div className="container">
        <div className="legal-head">
          <div className="eyebrow"><span>● Cookies</span></div>
          <h1>Cookie Notice</h1>
          <div className="legal-meta mono">Effective 1 February 2026 · Version 1.0</div>
          <p className="legal-lede">
            A cookie is a small text file your browser stores when you visit a website. We use them sparingly —
            to keep the site working, to learn (with your consent) how it's used, and to measure marketing effectiveness.
            You can change your choice any time using the button below.
          </p>
          <div style={{ marginTop: 18 }}>
            <button type="button" className="btn btn-primary" onClick={openPrefs} data-testid="cookies-open-prefs-btn">
              Manage cookie preferences <span className="arrow">→</span>
            </button>
          </div>
        </div>

        <div className="legal-body">
          <section className="legal-section">
            <h2>How we use cookies</h2>
            <p>
              We group cookies into three categories. Essential cookies always run because the site can't work without them.
              Analytics and Marketing cookies only run after you give consent.
            </p>
          </section>

          {TYPES.map((t) => (
            <section key={t.key} className="legal-section">
              <h2>
                {t.title}
                <span className={"legal-badge " + (t.key === "essential" ? "on" : "")}>{t.badge}</span>
              </h2>
              <p>{t.desc}</p>
              <div className="legal-table-wrap">
                <table className="legal-table" data-testid={`cookie-type-table-${t.key}`}>
                  <thead>
                    <tr><th>Name</th><th>Purpose</th><th>Retention</th><th>Provider</th></tr>
                  </thead>
                  <tbody>
                    {t.items.map((it, i) => (
                      <tr key={i}>
                        <td className="mono">{it.name}</td>
                        <td>{it.purpose}</td>
                        <td className="mono">{it.retention}</td>
                        <td>{it.provider}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}

          <section className="legal-section">
            <h2>How to control cookies in your browser</h2>
            <p>
              Most browsers let you block or delete cookies in settings. Doing so may break parts of the site (sign-in, theme,
              consent itself). For step-by-step instructions, see your browser's documentation —{" "}
              <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a>,{" "}
              <a href="https://support.mozilla.org/kb/clear-cookies-and-site-data-firefox" target="_blank" rel="noopener noreferrer">Firefox</a>,{" "}
              <a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a>,{" "}
              <a href="https://support.microsoft.com/microsoft-edge" target="_blank" rel="noopener noreferrer">Edge</a>.
            </p>
          </section>

          <section className="legal-section">
            <h2>Questions or requests</h2>
            <p>
              Email <a href="mailto:hello@capexara.com">hello@capexara.com</a> with the subject "Cookies".
              We respond within 30 days. See our <Link to="/privacy">Privacy Policy</Link> for the broader picture.
            </p>
          </section>

          <div className="legal-foot">
            <button type="button" className="btn btn-ghost" onClick={openPrefs}>Reopen cookie preferences</button>
            <Link to="/privacy" className="btn btn-primary">Privacy Policy <span className="arrow">→</span></Link>
          </div>
        </div>
      </div>
    </section>
  );
}
